/**
 * StoreyClassifier — THE POI→storey classification used by every surface that filters
 * POIs by floor (2026-07-06, D-7).
 *
 * Moved verbatim from AutodeskViewer's getPOIStorey family (location-hierarchy chain →
 * custom_data RVT-level fallback → level-index aliasing). Before this module existed the
 * 2D minimap used this rich hierarchy classifier while the panorama sphere icons used a
 * storey-type-only check plus Z-elevation fallback — the same POI could show on one
 * surface and be hidden on the other (the original March-2026 pain point).
 *
 * The component keeps thin useCallback wrappers (names + dep arrays preserved) so
 * consumer sites stay untouched; the model/POI-list-derived alias map is built by
 * buildRvtLevelAliases from whatever POI list the surface owns.
 */
import { normalizeLevelName, normalizeRvtStoreyName } from './StoreyNames';
import { parseCustomData } from '../../utils/POIFieldExtractor';

export const isStoreyType = (type: string | null | undefined): boolean => {
  if (!type) return false;
  const lower = type.toLowerCase();
  return lower.includes('storey') || lower.includes('level') || lower === 'floor';
};

export const isSpaceType = (type: string | null | undefined): boolean => {
  if (!type) return false;
  const lower = type.toLowerCase();
  return lower.includes('space') || lower.includes('room');
};

export const isNumericLevelValue = (value: string | null): boolean => {
  return !!value && /^-?\d+(\.\d+)?$/.test(value);
};

// .includes (not endsWith) — matches the original AutodeskViewer check verbatim; source
// strings can carry suffixes after the extension (e.g. versioned ACC names).
const hasRvtSource = (fileName: unknown): boolean =>
  typeof fileName === 'string' && fileName.toLowerCase().includes('.rvt');

const isRvtPOI = (poi: any): boolean => {
  const customData = parseCustomData(poi) || {};
  if (customData?.rvt) return true;
  if (hasRvtSource(poi?.ifc_source_file) || hasRvtSource(poi?.source_file) || hasRvtSource(poi?.location_source_file)) {
    return true;
  }
  if (Array.isArray(poi?.locations)) {
    return poi.locations.some((location: any) => hasRvtSource(location?.source_file));
  }
  return false;
};

const normalizeStoreyForPOI = (poi: any, value: unknown): string | null => {
  const normalized = normalizeLevelName(value);
  if (!normalized) return null;
  return isRvtPOI(poi) ? (normalizeRvtStoreyName(normalized) || normalized) : normalized;
};

export const getPOIRvtLevelFields = (poi: any): { levelNames: string[]; levelIndex: string | null } => {
  const customData = parseCustomData(poi) || {};
  const rvtProps = customData?.rvt?.rvtProperties || {};

  return {
    levelNames: [
      customData?.ifc?.properties?.Level,
      customData?.rvt?.level,
      rvtProps['Level'],
      rvtProps['IAA_Level'],
      rvtProps['Base Level'],
      customData?.rvt?.properties?.Level,
    ]
      .map(normalizeLevelName)
      .filter((value): value is string => !!value),
    levelIndex: normalizeLevelName(customData?.rvt?.rvtLevel),
  };
};

// Build the numeric-level-index → display-name alias map from a POI list: for each RVT
// level index, the most common textual level name carried by POIs on that index wins.
export const buildRvtLevelAliases = (pois: any[]): Map<string, string> => {
  const countsByIndex = new Map<string, Map<string, number>>();

  (pois || []).forEach((poi: any) => {
    const { levelNames, levelIndex } = getPOIRvtLevelFields(poi);
    if (!levelIndex) return;

    const textualLevelNames = levelNames.filter((levelName) => !isNumericLevelValue(levelName));
    if (textualLevelNames.length === 0) return;

    let indexCounts = countsByIndex.get(levelIndex);
    if (!indexCounts) {
      indexCounts = new Map<string, number>();
      countsByIndex.set(levelIndex, indexCounts);
    }

    textualLevelNames.forEach((levelName) => {
      indexCounts!.set(levelName, (indexCounts!.get(levelName) || 0) + 1);
    });
  });

  const aliases = new Map<string, string>();
  countsByIndex.forEach((nameCounts, levelIndex) => {
    let bestName: string | null = null;
    let bestCount = -1;
    nameCounts.forEach((count, levelName) => {
      if (count > bestCount) {
        bestName = levelName;
        bestCount = count;
      }
    });
    if (bestName) {
      aliases.set(levelIndex, bestName);
    }
  });

  return aliases;
};

// THE classifier: resolve a POI's storey display-name, or null when unassignable.
// Chain: direct storey location → parent storey → space's parent → any parent →
// location name → custom_data RVT level names → aliased level index → raw level index.
export const getStoreyNameForPOI = (
  poi: any,
  rvtLevelAliases: Map<string, string> = new Map()
): string | null => {
  // POI API JOINs with locations table; types: 'Building Storey', 'Spaces', 'Building'
  if (isStoreyType(poi?.location_type)) {
    return normalizeStoreyForPOI(poi, poi.location_name);
  }

  if (isStoreyType(poi?.parent_location_type)) {
    return normalizeStoreyForPOI(poi, poi.parent_location_name);
  }

  if (isSpaceType(poi?.location_type) && poi?.parent_location_name) {
    return normalizeStoreyForPOI(poi, poi.parent_location_name);
  }

  if (poi?.parent_location_name) {
    return normalizeStoreyForPOI(poi, poi.parent_location_name);
  }

  if (poi?.location_name) {
    return normalizeStoreyForPOI(poi, poi.location_name);
  }

  // Fallback to custom_data RVT level fields
  const { levelNames, levelIndex } = getPOIRvtLevelFields(poi);

  for (const levelName of levelNames) {
    if (!isNumericLevelValue(levelName)) {
      return normalizeRvtStoreyName(levelName) || levelName;
    }

    const aliasedLevelName = rvtLevelAliases.get(levelName);
    if (aliasedLevelName) {
      return normalizeRvtStoreyName(aliasedLevelName) || aliasedLevelName;
    }
  }

  if (levelIndex) {
    const aliasedLevelName = rvtLevelAliases.get(levelIndex);
    if (aliasedLevelName) {
      return normalizeRvtStoreyName(aliasedLevelName) || aliasedLevelName;
    }
    return levelIndex;
  }

  return null;
};
